import { useMemo } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';

import { cn } from '@/lib/utils';
import {
  useWorkspaceFileExistence,
  useWorkspaceFilePreviewLink,
} from '@/features/workspace-files';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { findWorkspacePaths } from '@/shared/components/markdownFilePaths';
import { MarkdownLazy } from '@/shared/components/MarkdownLazy';
import type { TextEntryRole } from '@/lib/api/generated/model';
import { DRAWER_PROSE_CLASS } from '../prose';
import {
  COST_FORMAT,
  formatDurationMs,
  formatInputValue,
  formatTurns,
} from '../executionHistory';
import { readRequestedEdit } from '../requestedEdit';
import { isErrorResult } from '../transcript';
import type { TranscriptEntry as TranscriptEntryData } from '../transcript';
import { EditBlockLazy } from './EditBlockLazy';
import { ToolBlock } from './ToolBlock';

export interface TranscriptEntryProps {
  entry: TranscriptEntryData;
  /** This execution's configured turn limit (spec 102) — not carried by the entry itself. */
  maxTurns?: number | null;
  /** How an edit's file is linked (spec 157); without it the path is text. */
  filePaths?: MarkdownFilePaths;
}

/**
 * One thing Claude did during a run, rendered per its `type`. Every variant is
 * a plain block — the phase grouping and the entry ordering are
 * `TranscriptEntryList`'s job, not this component's.
 *
 * Two of the four variants changed shape in spec 096. A `text` entry is a
 * message: rendered as the markdown it was written in, with a speaker mark, so
 * a heading is a heading and a person can tell who is talking without reading.
 * A `tool_call` or `tool_result` is a `ToolBlock`: folded until asked for, with
 * a failure open on arrival. `result` is the one line it always was. Since
 * spec 157 an editing call is an `EditBlock`, its change read as a diff.
 */
export function TranscriptEntry({
  entry,
  maxTurns,
  filePaths,
}: TranscriptEntryProps) {
  switch (entry.type) {
    case 'text':
      return <Message role={entry.role} content={entry.content} />;

    case 'tool_call': {
      // An edit reads as the change it asks for (spec 157); any input that is
      // not one of those shapes reads as it always did, as its JSON.
      const edit = readRequestedEdit(entry);
      if (edit) return <EditBlockLazy edit={edit} filePaths={filePaths} />;
      return (
        <ToolBlock
          title={entry.tool_name}
          summary={entry.summary}
          content={formatInputValue(entry.input)}
        />
      );
    }

    case 'tool_result': {
      const isError = isErrorResult(entry);
      return (
        <ToolBlock
          title={isError ? 'Tool error' : 'Tool result'}
          content={entry.content}
          tone={isError ? 'error' : 'default'}
          defaultOpen={isError}
        />
      );
    }

    case 'result':
      return (
        <div className="text-muted-foreground flex flex-wrap items-baseline gap-x-3 text-xs">
          {entry.duration_ms != null ? (
            <span>{formatDurationMs(entry.duration_ms)}</span>
          ) : null}
          {entry.cost != null ? (
            <span>{COST_FORMAT.format(entry.cost)}</span>
          ) : null}
          {entry.num_turns != null ? (
            <TurnsLabel numTurns={entry.num_turns} maxTurns={maxTurns} />
          ) : null}
          {entry.stop_reason ? (
            <span className="font-mono">{entry.stop_reason}</span>
          ) : null}
          <TokenUsage usage={entry.usage} />
        </div>
      );
  }
}

/**
 * "count/limit" when this run's turn limit is known (spec 102), otherwise the
 * bare count exactly as this line always read — never a fabricated limit.
 */
function TurnsLabel({
  numTurns,
  maxTurns,
}: {
  numTurns: number;
  maxTurns: number | null | undefined;
}) {
  const paired = formatTurns(numTurns, maxTurns);
  return (
    <span>
      {paired
        ? `${paired} turns`
        : `${numTurns} turn${numTurns === 1 ? '' : 's'}`}
    </span>
  );
}

/**
 * What each side of the session said (spec 096 FR-009).
 *
 * The contract's `user` role is not a person. Nobody types into an agent's
 * session: every `user` turn is text the run itself put there — the step's
 * prompt, a hook's feedback, a tool's follow-up — so it is labelled as an
 * instruction, and never with a name or an address, which would attribute
 * machine text to whoever happened to create the task. The person is on the
 * Details tab, where identity belongs.
 *
 * A border and a label rather than bubbles: the drawer has no width to spend on
 * bubble gutters, and the tool blocks around a message already carry borders of
 * their own. The label is the distinction; `border-primary` is a scan aid, not
 * the only cue.
 */
const ROLE_LABELS: Record<TextEntryRole, { label: string; title?: string }> = {
  assistant: { label: 'Assistant' },
  user: {
    label: 'Instruction',
    title: 'Sent to the agent by the run, not by a person',
  },
};

function Message({ role, content }: { role: TextEntryRole; content: string }) {
  const assistant = role === 'assistant';
  const { label, title } = ROLE_LABELS[role];
  const filePaths = useMessageFilePaths(content);
  return (
    <div
      className={cn(
        'flex flex-col gap-1 border-l-2 pl-3',
        assistant ? 'border-primary/60' : 'border-border',
      )}
    >
      <span className="text-muted-foreground text-xs font-medium" title={title}>
        {label}
      </span>
      <MarkdownLazy
        content={content}
        className={DRAWER_PROSE_CLASS}
        filePaths={filePaths}
      />
    </div>
  );
}

/**
 * The workspace paths a turn names, as links to the preview panel beside the
 * sheet (spec 140 FR-058) — the reviewer feedback names "file links rendered
 * in execution history or step output" explicitly.
 *
 * The workspace and the open preview come from the address rather than from
 * props: a transcript entry is four components below the drawer
 * (`TaskExecutionsPanel` → `ExecutionDetail` → `TranscriptEntryList`), and
 * every route that renders one carries `$workspaceId` and the `preview` param
 * the drawer itself reads. Threading both down for one link's destination
 * would put a preview concern in the signature of three components that have
 * nothing else to do with it. `strict: false` because the three routes are
 * different matches.
 *
 * No workspace in the address means no existence check and no links: the text
 * stays exactly as written, which is what it is today.
 */
function useMessageFilePaths(content: string): MarkdownFilePaths | undefined {
  const { workspaceId } = useParams({ strict: false });
  const search: { preview?: unknown } = useSearch({ strict: false });
  const previewPath =
    typeof search.preview === 'string' ? search.preview : undefined;
  const previewLink = useWorkspaceFilePreviewLink(
    workspaceId ?? '',
    previewPath,
  );
  const mentioned = useMemo(
    () => (workspaceId ? [...new Set(findWorkspacePaths(content))] : []),
    [content, workspaceId],
  );
  const existence = useWorkspaceFileExistence(workspaceId ?? '', mentioned);
  if (workspaceId === undefined) return undefined;
  return { exists: existence.exists, linkTo: previewLink };
}

/**
 * Input/output token counts, each as their own labeled value, when the
 * backend included them. `usage` is the Claude Agent SDK's own usage object
 * passed through verbatim — not a contract this codebase controls — so the
 * two keys read here are picked defensively rather than assumed to always be
 * present or numeric.
 */
function TokenUsage({ usage }: { usage: unknown }) {
  if (typeof usage !== 'object' || usage === null) return null;
  const record = usage as Record<string, unknown>;
  const input = record.input_tokens;
  const output = record.output_tokens;
  return (
    <>
      {typeof input === 'number' ? (
        <span className="font-mono">{input} in</span>
      ) : null}
      {typeof output === 'number' ? (
        <span className="font-mono">{output} out</span>
      ) : null}
    </>
  );
}
